import React, { useState, useEffect } from 'react';
import { Zap, X } from 'lucide-react';

const FlashSaleBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [flashSale, setFlashSale] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchFlashSale = async () => {
      try {
        const response = await fetch(`${API_URL}/api/flash-sale`);
        if (response.ok) {
          const data = await response.json();
          if (data.active) {
            setFlashSale(data);
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        }
      } catch (error) {
        console.error('Error fetching flash sale:', error);
      }
    };

    fetchFlashSale();
    const interval = setInterval(fetchFlashSale, 60 * 1000);
    return () => clearInterval(interval);
  }, [API_URL]);

  useEffect(() => {
    if (!flashSale?.end_time) return;

    const calculateTimeLeft = () => {
      const endTime = new Date(flashSale.end_time).getTime();
      const now = Date.now();
      const difference = endTime - now;

      if (difference <= 0) {
        setIsVisible(false);
        return { hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        hours: Math.floor(difference / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [flashSale]);

  if (!isVisible || !flashSale || dismissed) return null;

  const formatTime = (num) => String(num).padStart(2, '0');

  // Version minimisée - juste un petit badge
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-[90] bg-gradient-to-r from-orange-600 to-red-500 text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-2 hover:scale-105 transition-transform animate-pulse-subtle"
      >
        <Zap className="w-4 h-4" />
        <span className="font-bold">-{flashSale.discount_percent}%</span>
        <span className="text-xs opacity-80">{formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[90] animate-slide-in">
      <div className="bg-zinc-900/95 backdrop-blur-sm border border-orange-500/30 rounded-xl p-3 shadow-xl max-w-[280px]">
        {/* Header compact */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-500/20 rounded-md flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-orange-400" />
            </div>
            <span className="text-orange-400 text-xs font-semibold uppercase tracking-wide">Vente Flash</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 text-gray-500 hover:text-white transition-colors"
              title="Réduire"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 text-gray-500 hover:text-white transition-colors"
              title="Fermer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        
        {/* Content compact */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              -{flashSale.discount_percent}%
            </div>
            <p className="text-gray-400 text-[10px] leading-tight mt-0.5">
              {flashSale.title}
            </p>
          </div>
          
          {/* Timer compact */}
          <div className="bg-black/40 rounded-lg px-2.5 py-1.5 text-center">
            <div className="text-white font-mono font-bold text-sm">
              {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
            </div>
            <div className="text-gray-500 text-[9px] uppercase tracking-wider">restant</div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default FlashSaleBanner;
